import { useMutation } from "@apollo/client/react";
import MontuToastProvider from "@repo/components/src/globalProviders/toast";
import LoadingButton from "@repo/components/src/shared/loadingButton";
import { Toast, ToastDescription } from "@repo/components/src/ui/toast";
import { downloadFile } from "@repo/components/src/utils/downloadFile";
import { backendUrl } from "@repo/graphql/cache/url";
import { ExportPostCommentsDocument } from "@repo/graphql/documents/analytics/mutation.generated";
import { Download } from "lucide-react-native";
import React, { useCallback } from "react";

type Props = {
  brandId: string;
  postId: string;
};

const showErrorToast = (message: string) => {
  MontuToastProvider.show({
    placement: "bottom",
    duration: 3000,
    render: ({ id }) => (
      <Toast nativeID={`toast-${id}`} action="error" variant="solid">
        <ToastDescription size="sm">{message}</ToastDescription>
      </Toast>
    ),
  });
};

/**
 * Downloads every comment on this post as XLSX.
 *
 * Exports the whole post, not the sentiment tab currently selected: the tab
 * is a way to read the list, and a file that silently contained only the
 * negatives would be a trap. The server re-checks that the post belongs to
 * the brand, so the id travelling from here is not trusted.
 */
const ExportCommentsButton = React.memo(({ brandId, postId }: Props) => {
  const [runExport, { loading }] = useMutation(ExportPostCommentsDocument, {
    variables: { brandId, postId },
  });

  const handlePress = useCallback(async () => {
    try {
      const result = await runExport();
      const payload = result.data?.analytics.exportPostComments;
      if (!payload) return;
      if (payload.userErrors && payload.userErrors.length > 0) {
        showErrorToast(payload.userErrors[0].message);
        return;
      }
      downloadFile(
        payload.file.url,
        `comentarios-post-${postId}.xlsx`,
        backendUrl,
      );
    } catch (_err) {
      showErrorToast("Error al exportar los comentarios");
    }
  }, [runExport, postId]);

  return (
    <LoadingButton
      variant="outline"
      action="muted"
      size="xs"
      rounded="lg"
      onPress={handlePress}
      loading={loading}
      loadingText="Exportando…"
      startIcon={Download}
    >
      Exportar
    </LoadingButton>
  );
});

ExportCommentsButton.displayName = "ExportPostCommentsButton";

export default ExportCommentsButton;
